import { ChevronDown } from "lucide-react";
import React, { FC, ReactNode } from "react";

import {
  Accordion,
  AccordionDetailsProps,
  AccordionProps,
  AccordionSummary,
  AccordionSummaryProps,
  Grid,
  useTheme,
} from "@mui/material";
import AccordionDetails from "@mui/material/AccordionDetails";

import { IgChip } from "./IgChip";
import { IgTypography } from "./IgTypography";
import {
  IgBorderColors,
  IgCoreBackgroundColors,
  IgCustomTheme,
  IgShadows,
} from "./theme";

interface IgAccordionProps extends Omit<AccordionProps, "title"> {
  title: ReactNode;
  description?: string;
  customDescription?: ReactNode;
  headerIcon?: ReactNode;
  endHeaderIcon?: ReactNode;
  summaryActionButtons?: ReactNode[];
  hideSummaryActionButtons?: boolean;
  showOptionalChip?: boolean;
  accordionSummaryProps?: AccordionSummaryProps;
  accordionDetailsProps?: AccordionDetailsProps;
}

/**
 * @see Grid
 */
export const IgAccordion: FC<IgAccordionProps> = (props) => {
  const {
    title,
    description,
    summaryActionButtons,
    accordionSummaryProps,
    accordionDetailsProps,
    hideSummaryActionButtons,
    showOptionalChip,
    children,
    headerIcon,
    customDescription,
    endHeaderIcon,
    ...rest
  } = props;
  const theme = useTheme();

  return (
    <Accordion
      sx={{
        backgroundColor: IgCoreBackgroundColors.baseLightHigh,
        border: `1px solid ${IgBorderColors.core.default} !important`,
        padding:
          description || customDescription ? `12px 16px 16px 16px` : "4px 16px",
        "&:hover": {
          borderShadow: `${IgShadows.low} !important`,
          border: `1px solid ${IgBorderColors.core.hover} !important`,
        },
      }}
      {...rest}
    >
      <AccordionSummary
        sx={{
          paddingLeft: "0px",
          paddingRight: "0px",
          "& .MuiAccordionSummary-content": {
            margin: "0px 0px",
          },
        }}
        expandIcon={
          hideSummaryActionButtons ? null : (
            <ChevronDown
              color={(theme as IgCustomTheme).igIconColors.light}
              size={16}
            />
          )
        }
      >
        <Grid
          container
          direction={"row"}
          flexWrap={"nowrap"}
          alignItems={"center"}
          justifyContent={"space-between"}
          wrap={"nowrap"}
        >
          <Grid item container direction={"column"}>
            <Grid
              item
              container
              direction={"row"}
              xs={12}
              flexWrap={"nowrap"}
              wrap={"nowrap"}
              columnSpacing={1}
              alignItems={"center"}
            >
              {headerIcon && <Grid item>{headerIcon}</Grid>}
              <Grid item>
                <IgTypography variant={"h3"}>{title}</IgTypography>
              </Grid>
              {endHeaderIcon && <Grid item>{endHeaderIcon}</Grid>}
              {showOptionalChip && (
                <Grid item>
                  <IgChip
                    disabled={true}
                    clickable={false}
                    sx={{
                      border: "1px dashed black",
                    }}
                    size={"small"}
                    label={`Optional`}
                  />
                </Grid>
              )}
            </Grid>
            {description || customDescription ? (
              <Grid item>
                {customDescription ? (
                  customDescription
                ) : description ? (
                  <IgTypography
                    variant={"body2"}
                    color={theme.palette.text.secondary}
                  >
                    {description}
                  </IgTypography>
                ) : null}
              </Grid>
            ) : null}
          </Grid>
          {summaryActionButtons && (
            <Grid
              xs={4}
              item
              container
              justifyContent={"flex-end"}
              direction={"row"}
              mr={3}
            >
              {summaryActionButtons.map((button, index) => (
                <Grid item key={index} columnSpacing={1}>
                  {button}
                </Grid>
              ))}
            </Grid>
          )}
        </Grid>
      </AccordionSummary>
      <AccordionDetails
        sx={{ paddingLeft: "0px", paddingRight: "0px", marginTop: "4px" }}
      >
        {children}
      </AccordionDetails>
    </Accordion>
  );
};
